import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Extracted verbatim from the original Layout.js.
 * Same localStorage key, same mobile behavior, same resize handling,
 * same escape handling. Sidebar behavior does not change.
 */
export default function useSidebarState() {
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    if (window.innerWidth <= 768) return false;
    const saved = localStorage.getItem("crm-sidebar-open");
    return saved !== null ? saved === "true" : true;
  });

  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768,
  );

  const location = useLocation();

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      } else {
        const saved = localStorage.getItem("crm-sidebar-open");
        setSidebarOpen(saved !== null ? saved === "true" : true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close sidebar on Escape key (mobile)
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && sidebarOpen && isMobile) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [sidebarOpen, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      localStorage.setItem("crm-sidebar-open", String(next));
      return next;
    });
  };

  const closeSidebar = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return { sidebarOpen, toggleSidebar, closeSidebar, isMobile };
}